import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AboutYourCompany.module.css";
import SignupLayout from "../SignupLayout/SignupLayout";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCompanyProfileDetails,
  fetchUserProfile,
  getCompanyDetailsSuggestions,
  fetchStateSuggesionsInit,
  getPersonalDetailsLocation,
} from "../../../redux/actions";
import { CustomSelect } from "../components/CustomSelect/CustomSelect";
import { Spin } from "antd";
import { useHistory } from "react-router-dom";
import CongratsModal from "../components/Modals/CongratsModal";
import { getSelectedPlan, markUpgradeModalForSignupSuccess } from "../../../shared/billing";
import { brandfetchSearchApi, fetchTimezonesApi, fetchCurrenciesApi } from "../../../API/API";
import { getImage } from "../../../utilities/storageOperations/downloadURL";
import {ReactComponent as DropdownArrow} from '../../../assets/icons/DropdownArrow.svg';


const CURRENCY_OPTIONS = [
  { label: "USD ($)", value: "USD" },
  { label: "EUR (€)", value: "EUR" },
  { label: "GBP (£)", value: "GBP" },
  { label: "PKR (₨)", value: "PKR" },
  { label: "AED (د.إ)", value: "AED" },
  { label: "CAD (C$)", value: "CAD" },
  { label: "AUD (A$)", value: "AUD" },
  { label: "INR (₹)", value: "INR" },
];

const COUNTRY_CURRENCY_MAP = {
  "united states": "USD",
  usa: "USD",
  canada: "CAD",
  "united kingdom": "GBP",
  england: "GBP",
  pakistan: "PKR",
  "united arab emirates": "AED",
  uae: "AED",
  australia: "AUD",
  india: "INR",
  germany: "EUR",
  france: "EUR",
  italy: "EUR",
  spain: "EUR",
  ireland: "EUR",
  netherlands: "EUR",
};

const getCurrencyFromCountry = (countryItem) => {
  try {
    const directCurrency =
      countryItem?.Currency ||
      countryItem?.CurrencyCode ||
      countryItem?.PreferredCurrency ||
      countryItem?.currency ||
      countryItem?.currencyCode ||
      countryItem?.preferredCurrency;

    if (directCurrency) return String(directCurrency).toUpperCase();

    const countryName = String(countryItem?.CountryName || countryItem || "")
      .trim()
      .toLowerCase();

    return COUNTRY_CURRENCY_MAP[countryName] || "";
  } catch {
    return "";
  }
};

export default function AboutYourCompany() {
  const history = useHistory();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.getCompanyDetailsReducer?.loadingAbout);
  const dropdown = useSelector((state) => state.getCompanyDetailsReducer.dropdown);
  const token = useSelector((state) => state.authReducer?.user?.authToken);
  const profile = useSelector((state) => state?.profileReducer?.user?.profile);
  const stateSuggestions = useSelector((state) => state.searchSkillListingReducer?.stateSuggestions);
  const locationSuggestions = useSelector((state) => state.getPersonalDetailsLocationReducer?.location);

  const [companyName, setCompanyName] = useState(profile?.CompanyName || "");
  const [category, setCategory] = useState(profile?.Industries?.IndustryName || "");
  const [customCategory, setCustomCategory] = useState("");
  const [companySize, setCompanySize] = useState(profile?.CompanySize || "");
  const [logoKey, setLogoKey] = useState(profile?.PictureUrl || null);
  const [logoPreview, setLogoPreview] = useState("");
  const [country, setCountry] = useState(profile?.Countries?.CountryName || "");
  const [timezone, setTimezone] = useState(profile?.Timezone || "");
  const [timezoneValue, setTimezoneValue] = useState(profile?.Timezone || "");
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [currency, setCurrency] = useState(profile?.PreferredCurrency || "");
  const [currencyOptions, setCurrencyOptions] = useState(CURRENCY_OPTIONS);
  const currencySearchTimerRef = useRef(null);

  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [showTimezoneSuggestions, setShowTimezoneSuggestions] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);

  useEffect(() => { countryRef.current = country; }, [country]);

  useEffect(() => {
    if (locationSuggestions) setCountryLoading(false);
  }, [locationSuggestions]);

  useEffect(() => {
    if (!timezone.trim()) { setTimezoneOptions([]); setShowTimezoneSuggestions(false); setTimezoneLoading(false); return; }
    if (isSelectingTimezoneRef.current) { isSelectingTimezoneRef.current = false; return; }
    setTimezoneLoading(true);
    setShowTimezoneSuggestions(true);
    const timer = setTimeout(() => {
      fetchTimezonesApi({ limit: 25, country: timezone })
        .then((res) => {
          const list = res?.data?.timezones || [];
          setTimezoneOptions(list.map((tz) => ({ label: tz.displayText, value: tz.value })));
          setTimezoneLoading(false);
        })
        .catch(() => { setTimezoneLoading(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [timezone]);

  useEffect(() => {
    if (!showTimezoneSuggestions) return;
    const handler = (e) => {
      if (timezoneWrapRef.current && !timezoneWrapRef.current.contains(e.target))
        setShowTimezoneSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showTimezoneSuggestions]);

  useEffect(() => {
    if (!showCategorySuggestions) return;
    const handler = (e) => {
      if (categoryWrapRef.current && !categoryWrapRef.current.contains(e.target))
        setShowCategorySuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCategorySuggestions]);

  const countryWrapRef = useRef(null);
  const companyWrapRef = useRef(null);
  const timezoneWrapRef = useRef(null);
  const categoryWrapRef = useRef(null);
  const countryRef = useRef(country);
  const isSelectingLocationRef = useRef(false);
  const isSelectingCompanyRef = useRef(false);
  const isSelectingTimezoneRef = useRef(false);
  const isSelectingCategoryRef = useRef(false);

  useEffect(() => {
    if (token) dispatch(fetchUserProfile(token));
  }, [token, dispatch]);

  useEffect(() => {
    dispatch(getCompanyDetailsSuggestions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStateSuggesionsInit({}));
  }, [dispatch]);

  useEffect(() => {
    if (!profile) return;
    setCompanyName(profile?.CompanyName || "");
    if (profile?.PictureUrl) {
      setLogoKey(profile.PictureUrl);
      getImage(profile.PictureUrl)
        .then((url) => { if (url) setLogoPreview(url); })
        .catch(() => {});
    }
    const industryName = profile?.Industries?.IndustryName;
    if (industryName && dropdown?.industries) {
      const found = dropdown.industries.find((i) => i.IndustryName === industryName);
      if (found) setCategory(found.IndustryName);
    }
    if (
      profile?.CompanySizeId !== undefined &&
      profile?.CompanySizeId !== null &&
      dropdown?.company_sizes
    ) {
      const foundSize = dropdown.company_sizes.find(
        (item) => item.CompanySizeId === profile.CompanySizeId
      );
      if (foundSize) setCompanySize(foundSize.CompanySize);
    }
    if (profile?.Countries?.CountryName) setCountry(profile.Countries.CountryName);
    if (profile?.Timezone) { setTimezone(profile.Timezone); setTimezoneValue(profile.Timezone); }
    if (profile?.PreferredCurrency) setCurrency(profile.PreferredCurrency);
  }, [profile, dropdown]);

  const industryOptions = useMemo(() => {
    if (!dropdown?.industries) return [];
    return [
      ...dropdown.industries.map((item) => ({
        label: item.IndustryName,
        value: item.IndustryName,
      })),
      { label: "Other", value: "Other" },
    ];
  }, [dropdown]);

  const companySizeOptions = useMemo(() => {
    if (!dropdown?.company_sizes) return [];
    const getSizeOrder = (size) => {
      if (size === "Only me") return 0;
      const match = String(size).match(/\d+/);
      return match ? parseInt(match[0], 10) : Infinity;
    };
    return dropdown.company_sizes
      .slice()
      .sort((a, b) => getSizeOrder(a.CompanySize) - getSizeOrder(b.CompanySize))
      .map((item) => ({ label: item.CompanySize, value: item.CompanySize }));
  }, [dropdown]);

  const filteredIndustryOptions = useMemo(() => {
    if (!category.trim()) return industryOptions;
    return industryOptions.filter((opt) =>
      opt.label.toLowerCase().includes(category.toLowerCase())
    );
  }, [category, industryOptions]);

  const countryOptions = useMemo(() => {
    if (!stateSuggestions?.length) return [];
    return stateSuggestions.map((c) => ({
      label: c.CountryName,
      value: c.CountryName,
    }));
  }, [stateSuggestions]);

  useEffect(() => {
    if (!companyName.trim()) { setCompanySuggestions([]); setShowCompanySuggestions(false); setCompanyLoading(false); return; }
    if (isSelectingCompanyRef.current) { isSelectingCompanyRef.current = false; return; }
    setCompanyLoading(true);
    setShowCompanySuggestions(true);
    const timer = setTimeout(async () => {
      try {
        const res = await brandfetchSearchApi(companyName);
        const data = res?.data?.companies;
        if (Array.isArray(data) && data.length > 0) {
          setCompanySuggestions(
            data.map((item) => ({
              CompanyName: item.name || item.CompanyName,
              icon: item.logos?.[0]?.s3?.url || item.icon || null,
              logoKey: item.logos?.[0]?.s3?.key || item.icon || null,
            }))
          );
          setCompanyLoading(false);
          setShowCompanySuggestions(true);
        } else {
          setCompanySuggestions([]);
          setCompanyLoading(false);
          setShowCompanySuggestions(false);
        }
      } catch {
        setCompanySuggestions([]);
        setCompanyLoading(false);
        setShowCompanySuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [companyName]);

  useEffect(() => {
    if (!country.trim()) { setShowCountrySuggestions(false); setCountryLoading(false); return; }
    if (isSelectingLocationRef.current) { isSelectingLocationRef.current = false; return; }
    setCountryLoading(true);
    setShowCountrySuggestions(true);
    const timer = setTimeout(() => {
      dispatch(getPersonalDetailsLocation(country, "country"));
    }, 300);
    return () => clearTimeout(timer);
  }, [country, dispatch]);

  useEffect(() => {
    if (!showCountrySuggestions) return;
    const handler = (e) => {
      if (countryWrapRef.current && !countryWrapRef.current.contains(e.target))
        setShowCountrySuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCountrySuggestions]);

  useEffect(() => {
    if (!showCompanySuggestions) return;
    const handler = (e) => {
      if (companyWrapRef.current && !companyWrapRef.current.contains(e.target))
        setShowCompanySuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCompanySuggestions]);

  const isFinishDisabled =
    !companyName.trim() ||
    !category ||
    (category === "Other" && !customCategory.trim()) ||
    !companySize ||
    !country.trim() ||
    !timezoneValue ||
    !currency ||
    loading;

  const finishHandler = () => {
    if (isFinishDisabled) return;
    const payload = {
      CompanyName: companyName.trim(),
      CompanySize: companySize,
      PictureUrl: logoKey || null,
      WizardFlow: 602,
      Industries: { IndustryName: category === "Other" ? customCategory.trim() : category },
      Country: country.trim(),
      Timezone: timezoneValue,
      Currency: currency,
      onSuccess: () => { history.push("/create-your-first-job", { companyName: companyName.trim() }); },
      onError: () => {},
    };
    dispatch(updateCompanyProfileDetails(payload));
  };

  const onSelectCountry = (item) => {
    try {
      const countryName = item?.CountryName || "";
      const selectedCurrency = getCurrencyFromCountry(item);

      isSelectingLocationRef.current = true;
      setCountry(countryName);
      setShowCountrySuggestions(false);

      if (selectedCurrency) {
        setCurrency(selectedCurrency);
        setCurrencyOptions(CURRENCY_OPTIONS);
      } else if (countryName) {
        fetchCurrenciesApi({ search: countryName })
          .then((res) => {
            const list = res?.data?.data || [];
            if (list.length > 0) {
              setCurrency(list[0].code);
              setCurrencyOptions(list.map((c) => ({ label: c.display, value: c.code })));
            }
          })
          .catch(() => {});
      }

      setTimezoneLoading(true);
      fetchTimezonesApi({ limit: 25, country: countryName })
        .then((res) => {
          const list = res?.data?.timezones || [];
          setTimezoneOptions(list.map((tz) => ({ label: tz.displayText, value: tz.value })));
          setTimezoneLoading(false);
        })
        .catch(() => { setTimezoneLoading(false); });
    } catch {
      setTimezoneLoading(false);
      setShowCountrySuggestions(false);
    }
  };

  const handleCurrencySearch = (query) => {
    if (currencySearchTimerRef.current) clearTimeout(currencySearchTimerRef.current);
    if (!query.trim()) { setCurrencyOptions(CURRENCY_OPTIONS); return; }
    currencySearchTimerRef.current = setTimeout(() => {
      fetchCurrenciesApi({ search: query })
        .then((res) => {
          const list = res?.data?.data || [];
          if (list.length > 0) {
            setCurrencyOptions(list.map((c) => ({ label: c.display, value: c.code })));
          } else {
            setCurrencyOptions([]);
          }
        })
        .catch(() => { setCurrencyOptions([]); });
    }, 300);
  };

  const handleCongratsContinue = () => {
    setShowModal(false);
    const payload = {
      WizardFlow: 1000,
      onSuccess: () => {
        if (getSelectedPlan()) markUpgradeModalForSignupSuccess();
        history.push("/employerdashboard");
      },
      onError: () => {},
    };
    dispatch(updateCompanyProfileDetails(payload));
  };

  return (
    <>
      <SignupLayout
        step={1.1}
        totalSegments={3}
        heading="About Your Team"
        subHeading="We'll use this information to set up your company in Skillgigs and automate parts of the experience for you."
        onNext={finishHandler}
        isNextDisabled={isFinishDisabled}
        nextText={"Next"}
        nextLoading={loading}
        showBack={false}
        scrollable
      >
        {/* Company Info */}
        <div className={styles.sectionLabel}>
          Company Info <span className={styles.required}>*</span>
        </div>

        <div className={styles.autocompleteWrap} ref={companyWrapRef}>
          <div className={styles.companyInputWrapper}>
            {logoPreview && (
              <img src={logoPreview} alt="" className={styles.companyLogoInInput} />
            )}
            <input
              className={styles.companyNameInput}
              style={{ width: "100%", paddingLeft: logoPreview ? "50px" : "16px" }}
              value={companyName}
              placeholder="Company Name"
              onChange={(e) => {
                setCompanyName(e.target.value);
                if (!e.target.value.trim()) {
                  setShowCompanySuggestions(false);
                  setLogoPreview("");
                  setLogoKey(null);
                }
              }}
              onFocus={() => { if (companyName && companySuggestions?.length > 0) setShowCompanySuggestions(true); }}
            />
          </div>
          {showCompanySuggestions && (companyLoading || companySuggestions?.length > 0) && (
            <div className={styles.suggestionsList}>
              {companyLoading ? (
                <div className={styles.suggestionLoader}><Spin size="small" /></div>
              ) : companySuggestions.map((item, i) => (
                <div
                  key={i}
                  className={styles.suggestionItem}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isSelectingCompanyRef.current = true;
                    setCompanyName(item.CompanyName);
                    if (item.icon) {
                      setLogoPreview(item.icon);
                      setLogoKey(item.logoKey || item.icon);
                    }
                    setShowCompanySuggestions(false);
                  }}
                >
                  {item.icon && (
                    <img
                      src={item.icon}
                      alt=""
                      style={{ width: 20, height: 20, objectFit: "contain", borderRadius: 4, flexShrink: 0 }}
                    />
                  )}
                  {item.CompanyName}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.autocompleteWrap} ref={categoryWrapRef}>
          <input
            className={styles.control}
            value={category}
            placeholder="which best describes your company?"
            onChange={(e) => { setCategory(e.target.value); if (e.target.value !== "Other") setCustomCategory(""); setShowCategorySuggestions(true); }}
            onFocus={() => { if (industryOptions?.length > 0) setShowCategorySuggestions(true); }}
            style={{ paddingRight: 40 }}
          />
          <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "inline-flex", alignItems: "center", color: "#667085" }}>
            <DropdownArrow />
          </span>
          {showCategorySuggestions && filteredIndustryOptions?.length > 0 && (
            <div className={styles.suggestionsList}>
              {filteredIndustryOptions.map((item, i) => (
                <div
                  key={i}
                  className={styles.suggestionItem}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isSelectingCategoryRef.current = true;
                    setCategory(item.value);
                    if (item.value !== "Other") setCustomCategory("");
                    setShowCategorySuggestions(false);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {category === "Other" && (
          <input
            className={styles.control}
            value={customCategory}
            placeholder="which best describes your company?"
            onChange={(e) => setCustomCategory(e.target.value)}
            style={{ marginTop: 8 }}
          />
        )}

        {/* Number of Employees */}
        <div className={styles.sectionLabel} style={{ marginTop: 16 }}>
          Number of Employees <span className={styles.required}>*</span>
        </div>

        <div className={styles.pillRow}>
          {companySizeOptions.map((opt) => (  
            <button
              key={opt.value}
              type="button"
              className={`${styles.pill} ${companySize === opt.value ? styles.pillActive : ""}`}
              onClick={() => setCompanySize(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Location and Currency */}
        <div className={styles.sectionLabel} style={{ marginTop: 28 }}>
          Location and Currency <span className={styles.required}>*</span>
        </div>

        <div className={styles.autocompleteWrap} ref={countryWrapRef}>
          <input
            className={styles.control}
            value={country}
            placeholder="Country"
            onChange={(e) => setCountry(e.target.value)}
            onFocus={() => { if (country && locationSuggestions?.length > 0) setShowCountrySuggestions(true); }}
          />
          {showCountrySuggestions && (countryLoading || locationSuggestions?.length > 0) && (
            <div className={styles.suggestionsList}>
              {countryLoading ? (
                <div className={styles.suggestionLoader}><Spin size="small" /></div>
              ) : locationSuggestions.map((item, i) => (
                <div
                  key={i}
                  className={styles.suggestionItem}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelectCountry(item);
                  }}
                >
                  {item.CountryName}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.autocompleteWrap} ref={timezoneWrapRef}>
          <input
            className={styles.control}
            value={timezone}
            placeholder="Timezone"
            onChange={(e) => { setTimezone(e.target.value); setTimezoneValue(""); }}
            onFocus={() => { if (timezoneLoading || timezoneOptions?.length > 0) setShowTimezoneSuggestions(true); }}
          />
          {showTimezoneSuggestions && (timezoneLoading || timezoneOptions?.length > 0) && (
            <div className={styles.suggestionsList}>
              {timezoneLoading ? (
                <div className={styles.suggestionLoader}><Spin size="small" /></div>
              ) : timezoneOptions.map((item, i) => (
                <div
                  key={i}
                  className={styles.suggestionItem}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isSelectingTimezoneRef.current = true;
                    setTimezone(item.label);
                    setTimezoneValue(item.value);
                    setShowTimezoneSuggestions(false);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <CustomSelect
          label=""
          placeholder="Select currency"
          value={currency}
          open={isCurrencyOpen}
          setOpen={setIsCurrencyOpen}
          onChange={setCurrency}
          options={currencyOptions}
          showSearch={true}
          onSearch={handleCurrencySearch}
        />
      </SignupLayout>

      {showModal && (
        <CongratsModal
          open={showModal}
          onOk={handleCongratsContinue}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
}
